import { createSelector } from '@ngxs/store';
import { BusinessEmailTemplatesSelectors } from './business-email-templates/business-email-templates.selectors';
import { EmailTemplatesState } from './email-templates.state';
import { GlobalEmailTemplatesSelectors } from './global-email-templates/global-email-templates.selectors';
import { EmailTemplateWithUser } from './email-templates.models';
import {
	ClientSubType,
	EmailRecipientType,
} from '@models/global-email-templates';
import { JurisdictionDepartments } from '@models/jurisdictions';

export class EmailTemplatesSelectors {
	static $emailTemplates = createSelector(
		[
			BusinessEmailTemplatesSelectors.$getTemplates,
			GlobalEmailTemplatesSelectors.$getTemplates,
		],
		(
			businessTemplates,
			globalTemplates,
		): Record<string, EmailTemplateWithUser> => {
			return [...globalTemplates, ...businessTemplates].reduce(
				(acc, template) => {
					acc[template.id] = template;
					return acc;
				},
				{},
			);
		},
	);

	static $selectedEmailTemplate = createSelector(
		[
			EmailTemplatesState,
			BusinessEmailTemplatesSelectors.$getTemplateByIdWithVersion,
			GlobalEmailTemplatesSelectors.$getTemplateByIdWithVersion,
		],
		(state, businessSelector, globalSelector) => {
			if (!state || !state.selectedTemplateId || !state.selectedTemplateType) {
				return null;
			}

			if (state.selectedTemplateType === 'business') {
				return businessSelector(state.selectedTemplateId);
			} else if (state.selectedTemplateType === 'global') {
				return globalSelector(state.selectedTemplateId);
			}

			return null;
		},
	);

	static $allEmailTemplateOptions = createSelector(
		[EmailTemplatesSelectors.$emailTemplates],
		(templates) => {
			return Object.values(templates).map((template) => ({
				id: template.id,
				text: template.templateName,
			}));
		},
	);

	static $emailTemplateOptionsByRecipient = createSelector(
		[EmailTemplatesSelectors.$emailTemplates],
		(templates) =>
			(
				recipientType: EmailRecipientType,
				subType: JurisdictionDepartments | ClientSubType | null = null,
			) => {
				return Object.values(templates)
					.filter((template) => {
						if (template.emailRecipientType === EmailRecipientType.ALL) {
							return true;
						}
						if (template.emailRecipientType === recipientType) {
							if (subType) {
								if (subType === EmailRecipientType.ALL) {
									return true;
								}
								return template.emailRecipientSubType === subType;
							}
							return true;
						}
						return false;
					})
					.map((template) => ({
						id: template.id,
						text: template.templateName,
					}));
			},
	);
}
